import { useEffect, useState } from "react";

import { AppDialog, AppDialogTitle } from "@/components/ui/app-dialog";
import { ConfirmDeleteDialog } from "@/components/ui/confirm-delete-dialog";
import { MuiButton } from "@/components/ui/mui-button";
import { useRecordItemUsage } from "@/hooks/catalog-item-usage";
import { useCatalogItemDetail, useDeleteCatalogItem } from "@/hooks/catalog-items";
import { itemDetailIsLoading } from "@/lib/catalog-detail-loading";
import type { CatalogDetailItem } from "@/lib/catalog-item-detail";
import type { CatalogItemImageInput } from "@/lib/catalog-item-image";
import {
  displayedImages,
  displayedVariant,
  ownItemSelection,
  resolveVariantSelection,
  switchesToAddedVariant,
} from "@/lib/catalog-item-variants";
import { itemDetailUrl } from "@/lib/route-paths";

import { AddVariantDialog } from "./add-variant-dialog";
import { announceItemDeleted, useItemsError } from "@/hooks/items-error";
import { DELETE_ITEM_ERROR, ItemDetailPage } from "./item-detail-page";

// The catalog's item details **in a dialog, read-only** — the donor's
// `ItemDetailsIsland` at `readOnly` in its dialog rendering mode, which the
// spec details page mounts once and opens from a row's thumbnail or item name
// (`pages/spec-details-page.tsx:28-34`, `ItemDetailsIsland.tsx:211-221`). The
// details page is its first consumer (#695); the Editor opens the same one.
//
// **The same island, not a second drawing of it**: the badges, the gallery, the
// manufacturer and name, the variant chips, the details table with its usage
// row, the manufacturer-site button and the description are the details page's
// own parts (`ItemDetailPage` at `readOnly`). What the mode changes is the
// donor's: no page frame, no CTAs, and a menu holding only "Report".
//
// The shell is the modsy `Dialog` at `fullWidth`, `maxWidth='md'`, with no
// title — so its X floats at 24px / 40px over the content — and it closes from
// the X, the backdrop and Escape (`react-ui/src/dialog/dialog.tsx`).
//
// The variant chips still switch the item shown, as they do in the donor's
// read-only dialog; the choice is the dialog's state and goes when it closes.
// The item read does not filter to alive items, so a retired item opens.
//
// **The Item Search island opens it in its CTA mode** (#798, `cta`): the same
// island in dialog mode *with* its CTAs — `Select` or `Remove Selected` aimed
// at the editor's active line and selection, `Add Variant`, and the details
// page's full menu in its dialog arrangement. In that mode the dialog owns the
// item verbs the item page route owns on the page — the delete with its
// confirmation and snackbars, Copy Link, and Add Variant with its dialog —
// because the outcomes are the dialog's own: it closes when the item it opened
// on is deleted, falls back to that item when a variant it switched to is,
// and switches to a variant added from it. The read-only mode is untouched.

/**
 * What the row the dialog opens from already knows about its item — the
 * donor's `{id, name, heroImgUrl}` (`LineDetailsRow.tsx:106-113`). A spec
 * line's item carries all three on its own read (`specItemSummaryWire`), so
 * nothing new crosses the wire for it.
 */
export interface ItemDialogSeed {
  id: number;
  name: string;
  heroImage: CatalogItemImageInput | null;
  /**
   * The opener's own copy of the item, when it holds one — the Item Search
   * island's card, or the island's loaded copy of a row's item: the donor's
   * object cache, which its dialog draws the ⋮, the badges and the
   * manufacturer from on the first frame (#964 N16).
   */
  item?: CatalogDetailItem | null;
  /**
   * The opener's copy of the item's whole gallery, hero first — the donor's
   * search item carries `images`, so its dialog draws the thumbnail strip on
   * the first frame (#964 N16). Absent, the hero alone stands in.
   */
  images?: CatalogItemImageInput[];
}

/** The line and selection a `Select` lands on — the editor's active pair. */
export interface ItemDialogTarget {
  lineId: number;
  selectionId: number;
}

/**
 * The Item Search island's CTA mode — the donor's `activeSelection` as the
 * dialog stores it, plus the two verbs, all computed by the host (#811).
 */
export interface ItemDialogCta {
  /**
   * Where `Select` lands, or `null` with no active selection — then only Add
   * Variant shows. The donor carries `targetSelection: { id, lineId }` with
   * `lineId` always undefined (its observer never stores it); Atlas carries
   * both (#767 R2).
   */
  target: ItemDialogTarget | null;
  /** The active selection's item, which the shown variant may be. */
  selectedItemId: number | null;
  /** The three gates of `CtaButtons`' `isDisabled`, and the menu's own. */
  isFinalized: boolean;
  isPerPlan: boolean;
  isSoftDeleted: boolean;
  /**
   * Whether this session edits every item regardless of division — what Edit
   * and Delete fall back to when the item's read fails, as on the item page.
   */
  mayEditAnyItem: boolean;
  /** `Select`, with the variant shown and the target. Called once per click. */
  onSelect: (item: CatalogDetailItem, target: ItemDialogTarget) => void;
  /** `Remove Selected`, with the variant shown. Called once per click. */
  onRemove: (item: CatalogDetailItem) => void;
}

interface ItemDetailDialogProps {
  /** The item to show, as its row knows it, or `null` while closed. */
  item: ItemDialogSeed | null;
  onClose: () => void;
  /** The Item Search island's CTA mode; absent, the dialog is read-only. */
  cta?: ItemDialogCta;
  /**
   * Whether the close hands focus back to the opener — on unless told
   * otherwise. Read at the close, so it must still hold the opening's choice
   * when `item` turns `null` (#896 D1 b: the island's mouse-opened name).
   */
  restoreFocus?: boolean;
}

export function ItemDetailDialog({ item, onClose, cta, restoreFocus }: ItemDetailDialogProps) {
  if (cta) {
    return (
      <ItemDetailCtaDialog item={item} onClose={onClose} cta={cta} restoreFocus={restoreFocus} />
    );
  }

  return (
    <AppDialog
      open={item !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      size="md"
      titleInBody
      restoreFocus={restoreFocus}
    >
      {item !== null ? <ItemDetailDialogContent key={item.id} seed={item} /> : null}
    </AppDialog>
  );
}

function ItemDetailDialogContent({
  seed,
  cta,
  onRequestDelete,
}: {
  seed: ItemDialogSeed;
  cta?: ItemDialogCta;
  /** CTA mode: the Delete row was pressed for this id; `opensOnIt` when it is the item opened on. */
  onRequestDelete?: (itemId: number, opensOnIt: boolean) => void;
}) {
  const itemId = seed.id;
  const detail = useCatalogItemDetail(itemId);
  const data = detail.data ?? null;

  // The variant shown — the item page's own mechanism (`routes/_authed/_chrome/
  // items/$itemId.tsx`), keyed here by the dialog's mount rather than a route.
  const [selection, setSelection] = useState(() => ownItemSelection(itemId));
  const displayedId = resolveVariantSelection(selection, itemId);
  const shown = data ? displayedVariant(data.item, data.variants, displayedId) : null;
  const shownId = shown?.id ?? itemId;

  // A sibling's usage is not in the group in hand, so showing one reads it —
  // the page's own second read.
  const showingSibling = shownId !== itemId;
  const siblingDetail = useCatalogItemDetail(shownId, { enabled: showingSibling });
  const usage = showingSibling ? siblingDetail : detail;
  // The dialog is a usage reader once its usage is asked for — its ⋮ opened or
  // its Spec Usage expanded, the presses the donor's usage read fires on
  // (`Header/ActionMenu.tsx:40-56`, `SpecUsage.tsx:74-86`). What it holds for
  // the item asked about then answers every card kebab for that item, as the
  // donor's usage entry is one per item that all its readers share (#964 N17).
  // Opening the dialog alone asks for nothing: `load-item` ships no usage.
  const recordUsage = useRecordItemUsage();
  const [usageAskedFor, setUsageAskedFor] = useState<number | null>(null);
  const usageData = usage.data;
  useEffect(() => {
    if (!usageData || usageData.item.id !== usageAskedFor) return;
    recordUsage(usageData.item.id, {
      mayEdit: usageData.mayEdit,
      usageCount: usageData.item.usageCount,
    });
  }, [usageData, usageAskedFor, recordUsage]);
  const [addingVariant, setAddingVariant] = useState(false);

  // The CTA mode's verbs — the item page route's, with the dialog's outcomes.
  const verbs = cta
    ? {
        readOnly: false as const,
        inDialog: true,
        finalized: cta.isFinalized,
        selectionCta: shown ? <SelectionCtaButtons shown={shown} cta={cta} /> : null,
        // As on the item page: the displayed variant's own `mayEdit`, and the
        // unrestricted roles' answer when that read failed.
        canEdit: usage.data ? usage.data.mayEdit : usage.isError && cta.mayEditAnyItem,
        onDeleteRequested: () => {
          // A switched-to variant: fall back to the item opened on, and stay.
          if (showingSibling) setSelection(ownItemSelection(itemId));
          onRequestDelete?.(shownId, !showingSibling);
        },
        onCopyLink: () => {
          // The item's own page, as the item page's Copy Link copies. The
          // donor also writes the spec and version ids to local storage for
          // its page-mode delete to return to; no screen reads them back
          // (#798, assumption 9), so nothing observable is left behind.
          void navigator.clipboard.writeText(itemDetailUrl(shownId));
        },
        onAddVariant: () => setAddingVariant(true),
      }
    : ({ readOnly: true } as const);

  return (
    <>
      {/* The dialog's name. The donor's has none on screen, and Radix wants one. */}
      <AppDialogTitle className="sr-only">{shown?.name ?? seed.name}</AppDialogTitle>
      <ItemDetailPage
        {...verbs}
        item={shown}
        // **The row's partial item stands in while the read is in flight** —
        // the donor seeds it into its item cache "to display a more interesting
        // loader" (`ItemDetailsIsland/data/observers.ts:50-64`), so its name,
        // its hero and the ⋮ draw over the skeleton instead of a blank column
        // (#718 B1).
        placeholder={{
          id: seed.id,
          name: seed.name,
          images: seed.images ?? (seed.heroImage ? [seed.heroImage] : []),
          item: seed.item,
        }}
        variants={data?.variants ?? []}
        onSelectVariant={(variantId) => setSelection({ baseId: itemId, variantId })}
        images={displayedImages(data, shown)}
        coreUsageCount={usage.data?.coreUsageCount ?? 0}
        divisionUsage={usage.data?.divisionUsage ?? []}
        breakdownArrived={usage.data !== undefined}
        isLoading={itemDetailIsLoading(detail)}
        isError={detail.isError && !data}
        isUsageLoading={usage.isPending || usage.isFetching}
        isUsageError={usage.isError && Boolean(data)}
        onUsageAsked={() => setUsageAskedFor(shownId)}
      />
      {cta && (
        // Over the details dialog, as the donor's `AddVariantIsland` is, and
        // added as a sibling of the variant shown. The dialog switches to the
        // new variant only when it was added from the item it opened on —
        // the donor's observer, `itemId === activeItemId`.
        <AddVariantDialog
          open={addingVariant}
          onOpenChange={setAddingVariant}
          parent={shown}
          variants={data?.variants ?? []}
          onAdded={(variantId) => {
            if (!switchesToAddedVariant(itemId, shownId)) return;
            setSelection({ baseId: itemId, variantId });
          }}
        />
      )}
    </>
  );
}

/**
 * The CTA mode's shell: the dialog, plus the delete it hands its Delete row to.
 *
 * **The confirmation lives beside the dialog, not in it**, because the donor's
 * dialog-mode observer acts on the Delete *click* — its comment says "when a
 * delete is successful", but it is wired to `deleteItemClickAction`
 * (`ItemDetailsIsland/data/observers.ts`) — so the dialog has closed (or fallen
 * back to the item it opened on) by the time the confirmation asks. The
 * confirmation and its outcomes are `ItemDeleteIsland`'s: closed and a success
 * snackbar on success, held open with the error snackbar on failure.
 */
function ItemDetailCtaDialog({
  item,
  onClose,
  cta,
  restoreFocus,
}: {
  item: ItemDialogSeed | null;
  onClose: () => void;
  cta: ItemDialogCta;
  restoreFocus?: boolean;
}) {
  const remove = useDeleteCatalogItem();
  const [pendingDelete, setPendingDelete] = useState<number | null>(null);
  useItemsError(remove.isError, DELETE_ITEM_ERROR, { onClose: () => remove.reset() });

  return (
    <>
      <AppDialog
        open={item !== null}
        onOpenChange={(open) => {
          if (!open) onClose();
        }}
        size="md"
        titleInBody
        restoreFocus={restoreFocus}
      >
        {item !== null ? (
          <ItemDetailDialogContent
            key={item.id}
            seed={item}
            cta={cta}
            onRequestDelete={(deletedId, opensOnIt) => {
              // Deleting the item the dialog opened on closes it; deleting a
              // variant it switched to is handled inside (it falls back).
              if (opensOnIt) onClose();
              setPendingDelete(deletedId);
            }}
          />
        ) : null}
      </AppDialog>
      <ConfirmDeleteDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => {
          if (!open) setPendingDelete(null);
        }}
        label="item"
        isDeleting={remove.isPending}
        onConfirm={() => {
          if (pendingDelete === null) return;
          const itemId = pendingDelete;
          remove.mutate(
            { itemId },
            {
              onSuccess: () => {
                setPendingDelete(null);
                announceItemDeleted(itemId);
              },
            },
          );
        }}
      />
    </>
  );
}

/**
 * `CtaButtons`' selection half: `Remove Selected` when the variant shown is the
 * active selection's item, else `Select` when there is a selection to land on —
 * both large and full width, both greyed out by the line's gates.
 */
function SelectionCtaButtons({ shown, cta }: { shown: CatalogDetailItem; cta: ItemDialogCta }) {
  const disabled = cta.isFinalized || cta.isPerPlan || cta.isSoftDeleted;
  const target = cta.target;

  if (cta.selectedItemId === shown.id) {
    return (
      <MuiButton
        data-testid="pdp-dialog-remove-btn"
        variant="outlined"
        size="large"
        fullWidth
        disabled={disabled}
        onClick={() => cta.onRemove(shown)}
      >
        Remove Selected
      </MuiButton>
    );
  }

  if (target === null) return null;

  return (
    <MuiButton
      data-testid="pdp-dialog-select-btn"
      variant="contained"
      size="large"
      fullWidth
      disabled={disabled}
      onClick={() => cta.onSelect(shown, target)}
    >
      Select
    </MuiButton>
  );
}
